import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useAuthSession } from '../features/auth/session'
import { useTheme } from '@/app/ThemeProvider'
import { LanguageSwitcher } from '@/components/app/LanguageSwitcher'
import { PublicFooter } from '@/components/app/PublicFooter'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarImage } from '@/components/ui/avatar'
import { AgentAvatarFallback } from '@/components/ui/avatar'
import { Skeleton } from '@/components/ui/skeleton'
import { avatarSeedForUser } from '@/lib/avatar-fallback'
import { env } from '@/lib/env'
import { fetchPublicLegalDocuments } from '@/features/legal/api'
import { NotificationBell } from '@/features/notifications/components/NotificationBell'
import {
  LogOut,
  Moon,
  PanelLeft,
  Sun,
  ChevronDown,
  User,
  Settings,
  ArrowLeft,
  Scale,
  FileText,
  Shield,
  Cookie,
  Briefcase,
  UserCheck,
  Monitor,
  Clock,
  BookOpen,
} from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

const DOCUMENT_TYPE_TO_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  platform_terms: FileText,
  platform_privacy: Shield,
  platform_cookies: Cookie,
  business_terms: Briefcase,
  business_privacy: Shield,
  agent_terms: UserCheck,
  agent_privacy: Shield,
}

const DOCUMENT_TYPE_TO_PATH: Record<string, string> = {
  platform_terms: '/legal/terms',
  platform_privacy: '/legal/privacy',
  platform_cookies: '/legal/cookies',
  business_terms: '/legal/business_terms',
  business_privacy: '/legal/business_privacy',
  agent_terms: '/legal/agent_terms',
  agent_privacy: '/legal/agent_privacy',
}

const DOCUMENT_TYPE_TO_LABEL: Record<string, string> = {
  platform_terms: 'legal.nav.terms',
  platform_privacy: 'legal.nav.privacy',
  platform_cookies: 'legal.nav.cookies',
  business_terms: 'legal.nav.businessTerms',
  business_privacy: 'legal.nav.businessPrivacy',
  agent_terms: 'legal.nav.agentTerms',
  agent_privacy: 'legal.nav.agentPrivacy',
}

const DOCUMENT_TYPE_ORDER = [
  'platform_terms',
  'platform_privacy',
  'platform_cookies',
  'business_terms',
  'business_privacy',
  'agent_terms',
  'agent_privacy',
]

export function PublicLegalLayout() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading, user, logout, logoutPending } = useAuthSession()
  const { theme, resolvedTheme, setTheme } = useTheme()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false
    const w = window.innerWidth
    return w >= 768 && w < 1280
  })
  const [sidebarMode, setSidebarMode] = useState<'drawer' | 'collapsed-desktop' | 'normal-desktop'>(() => {
    if (typeof window === 'undefined') return 'normal-desktop'
    const w = window.innerWidth
    if (w < 768) return 'drawer'
    if (w < 1280) return 'collapsed-desktop'
    return 'normal-desktop'
  })
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const isMobile = sidebarMode === 'drawer'

  const userInitial = user?.display_name?.trim().charAt(0).toUpperCase() ?? 'U'

  // Fetch all legal documents for sidebar (including inactive)
  const { data: docsData, isLoading: sidebarLoading } = useQuery({
    queryKey: ['public-legal-documents', i18n.language],
    queryFn: () => fetchPublicLegalDocuments(i18n.language, true),
    staleTime: 60_000,
  })

  const navGroups = useMemo(() => {
    if (!docsData?.data) return { platform: [], business: [], agent: [] }

    const allTypes = new Set(docsData.data.map((doc) => doc.type))
    const typeToActive: Record<string, boolean> = {}
    docsData.data.forEach((doc) => {
      typeToActive[doc.type] = doc.is_active
    })

    const buildGroup = (prefix: string) =>
      Array.from(allTypes)
        .filter((t) => t.startsWith(prefix))
        .sort((a, b) => {
          const idxA = DOCUMENT_TYPE_ORDER.indexOf(a)
          const idxB = DOCUMENT_TYPE_ORDER.indexOf(b)
          return idxA - idxB
        })
        .map((type) => ({
          path: DOCUMENT_TYPE_TO_PATH[type] || `/legal/${type}`,
          label: DOCUMENT_TYPE_TO_LABEL[type] || type,
          icon: DOCUMENT_TYPE_TO_ICON[type] || FileText,
          isActive: typeToActive[type] ?? false,
        }))

    const platform = buildGroup('platform_')
    const business = buildGroup('business_')
    const agent = buildGroup('agent_')

    return { platform, business, agent }
  }, [docsData])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mobileQuery = window.matchMedia('(max-width: 767px)')
    const compactQuery = window.matchMedia('(min-width: 768px) and (max-width: 1279px)')

    const syncMode = () => {
      if (mobileQuery.matches) {
        setSidebarMode('drawer')
        setMobileSidebarOpen(false)
      } else if (compactQuery.matches) {
        setSidebarMode('collapsed-desktop')
        setSidebarCollapsed(true)
        setMobileSidebarOpen(false)
      } else {
        setSidebarMode('normal-desktop')
        setMobileSidebarOpen(false)
      }
    }

    syncMode()
    mobileQuery.addEventListener('change', syncMode)
    compactQuery.addEventListener('change', syncMode)

    return () => {
      mobileQuery.removeEventListener('change', syncMode)
      compactQuery.removeEventListener('change', syncMode)
    }
  }, [])

  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [location.pathname])

  const visuallyCollapsed = isMobile ? false : sidebarCollapsed

  const renderNavItem = (item: {
    path: string
    label: string
    icon: React.ComponentType<{ className?: string }>
    isActive: boolean
  }) => {
    const Icon = item.icon
    const isPathActive = location.pathname === item.path

    return (
      <li key={item.path}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={item.path}
              className={[
                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                isPathActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                visuallyCollapsed ? 'justify-center px-2' : '',
              ].join(' ')}
            >
              <Icon className="h-4.5 w-4.5 shrink-0" />
              {!visuallyCollapsed && (
                <span className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="font-medium leading-snug line-clamp-2">{t(item.label)}</span>
                  {!item.isActive && (
                    <Clock className="h-3 w-3 shrink-0 text-amber-500" />
                  )}
                </span>
              )}
            </Link>
          </TooltipTrigger>
          {visuallyCollapsed && (
            <TooltipContent side="right">
              <span className="flex items-center gap-2">
                {t(item.label)}
                {!item.isActive && <Clock className="h-3 w-3 text-amber-500" />}
              </span>
            </TooltipContent>
          )}
        </Tooltip>
      </li>
    )
  }

  const sidebarContent = (
    <aside
      className={[
        'flex h-screen flex-col text-foreground transition-[width] duration-300',
        'bg-background',
        "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-gradient-to-b after:from-transparent after:via-border/70 after:to-transparent",
        isMobile
          ? 'w-64 shrink-0'
          : ['relative z-40', sidebarCollapsed ? 'w-16' : 'w-64', 'sticky top-0 shrink-0'].join(' '),
      ].join(' ')}
    >
      <div className="flex h-16 items-center px-3">
        {visuallyCollapsed ? (
          <div className="flex w-full items-center justify-center">
            <img
              src="/Uploads/logo-mark-light.svg"
              alt="Myhd mark"
              className="h-9 w-9 dark:hidden"
            />
            <img
              src="/Uploads/logo-mark-dark.svg"
              alt="Myhd mark"
              className="hidden h-9 w-9 dark:block"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <img src="/Uploads/logo-light.svg" alt="Myhd" className="h-9 w-auto dark:hidden" />
            <img src="/Uploads/logo-dark.svg" alt="Myhd" className="hidden h-9 w-auto dark:block" />
            <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              v{env.appVersion}
            </span>
          </div>
        )}
      </div>

      <TooltipProvider delayDuration={100}>
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <ul className="space-y-1">
            {isAuthenticated && (
              <li>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/dashboard"
                      className={[
                        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                        'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                        visuallyCollapsed ? 'justify-center px-2' : '',
                      ].join(' ')}
                    >
                      <ArrowLeft className="h-4.5 w-4.5 shrink-0" />
                      {!visuallyCollapsed && (
                        <span className="truncate font-medium">{t('legal.backToDashboard', 'Back to Dashboard')}</span>
                      )}
                    </Link>
                  </TooltipTrigger>
                  {visuallyCollapsed && (
                    <TooltipContent side="right">{t('legal.backToDashboard', 'Back to Dashboard')}</TooltipContent>
                  )}
                </Tooltip>
              </li>
            )}

            {sidebarLoading && (
              <>
                {!visuallyCollapsed ? (
                  <>
                    <li className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-4.5 w-4.5 shrink-0 rounded" />
                        <Skeleton className="h-3.5 w-3/4" />
                      </div>
                    </li>
                    <li className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-4.5 w-4.5 shrink-0 rounded" />
                        <Skeleton className="h-3.5 w-2/3" />
                      </div>
                    </li>
                    <li className="px-3 py-2">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-4.5 w-4.5 shrink-0 rounded" />
                        <Skeleton className="h-3.5 w-4/5" />
                      </div>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="px-3 py-2 flex justify-center">
                      <Skeleton className="h-4.5 w-4.5 rounded" />
                    </li>
                    <li className="px-3 py-2 flex justify-center">
                      <Skeleton className="h-4.5 w-4.5 rounded" />
                    </li>
                    <li className="px-3 py-2 flex justify-center">
                      <Skeleton className="h-4.5 w-4.5 rounded" />
                    </li>
                  </>
                )}
              </>
            )}

            {!sidebarLoading &&
              navGroups.platform.length === 0 &&
              navGroups.business.length === 0 &&
              navGroups.agent.length === 0 &&
              !visuallyCollapsed && (
                <li className="px-3 py-2">
                  <span className="text-xs text-muted-foreground">{t('legal.noDocuments', 'No documents available')}</span>
                </li>
              )}

            {!sidebarLoading && (
              <>
                {/* Platform section */}
                {navGroups.platform.length > 0 && !visuallyCollapsed && (
                  <li className="px-3 pt-3 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('legal.nav.platform', 'Platform')}
                    </span>
                  </li>
                )}
                {navGroups.platform.map((item) => renderNavItem(item))}

                {/* Business section */}
                {navGroups.business.length > 0 && !visuallyCollapsed && (
                  <li className="px-3 pt-3 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('legal.nav.business', 'Business')}
                    </span>
                  </li>
                )}
                {navGroups.business.map((item) => renderNavItem(item))}

                {/* Agent section */}
                {navGroups.agent.length > 0 && !visuallyCollapsed && (
                  <li className="px-3 pt-3 pb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t('legal.nav.agent', 'Agent')}
                    </span>
                  </li>
                )}
                {navGroups.agent.map((item) => renderNavItem(item))}
              </>
            )}
          </ul>
        </nav>
      </TooltipProvider>

    </aside>
  )

  const headerContent = (
    <header className="sticky top-0 z-20 w-full bg-background">
      <div className="flex h-14 w-full items-center gap-4 px-4 md:px-6">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => {
              if (sidebarMode === 'drawer') {
                setMobileSidebarOpen((value) => !value)
                return
              }
              setSidebarCollapsed((value) => !value)
            }}
            className="-ml-1"
            aria-label={
              sidebarMode === 'drawer'
                ? mobileSidebarOpen
                  ? t('navigation.sidebar.close')
                  : t('navigation.sidebar.open')
                : sidebarCollapsed
                  ? t('navigation.sidebar.expand')
                  : t('navigation.sidebar.collapse')
            }
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
          <Separator
            orientation="vertical"
            className="mr-2 hidden data-[orientation=vertical]:h-4 md:block"
          />
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-sm font-semibold">{t('legal.pageTitle', 'Legal')}</h1>
          </div>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <LanguageSwitcher />

          <Button
            onClick={() =>
              setTheme((current) => {
                const base = current === 'system' ? resolvedTheme : current
                return base === 'dark' ? 'light' : 'dark'
              })
            }
            variant="ghost"
            size="icon"
            className="relative"
            aria-label={
              resolvedTheme === 'dark'
                ? t('theme.switchToLight')
                : t('theme.switchToDark')
            }
          >
            {resolvedTheme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>

          {authLoading ? (
            <div className="hidden md:flex items-center gap-2">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-3.5 w-24" />
            </div>
          ) : isAuthenticated ? (
            <>
              <NotificationBell />
              <Separator
                orientation="vertical"
                className="hidden bg-border/80 data-[orientation=vertical]:h-4 md:block"
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-8 gap-2 px-2 text-left"
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={user?.avatar_url ?? undefined} alt={user?.display_name ?? 'User'} />
                      <AgentAvatarFallback
                        seed={user ? avatarSeedForUser({ id: user.id }) : 'anonymous'}
                        className="text-xs font-semibold"
                      >
                        {userInitial}
                      </AgentAvatarFallback>
                    </Avatar>
                    <span className="hidden md:block">
                      <span className="block max-w-[140px] truncate text-xs font-semibold text-foreground">
                        {user?.display_name ?? 'User'}
                      </span>
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="truncate text-sm font-medium">{user?.display_name ?? 'User'}</span>
                      <span className="truncate text-xs text-muted-foreground">{user?.email ?? ''}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/settings')}
                    className="cursor-pointer"
                  >
                    <User className="h-4 w-4" />
                    <span>{t('settings.personalProfile')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/settings')}
                    className="cursor-pointer"
                  >
                    <Settings className="h-4 w-4" />
                    <span>{t('settings.title')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/legal')}
                    className="cursor-pointer"
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>{t('legal.pageTitle', 'Legal')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5">
                    <p className="mb-2 text-xs font-medium text-muted-foreground">
                      {t('theme.title')}
                    </p>
                    <Tabs
                      value={theme}
                      onValueChange={(value) =>
                        setTheme(value as 'light' | 'dark' | 'system')
                      }
                    >
                      <TabsList className="w-full">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TabsTrigger value="light" className="px-2">
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center justify-center">
                                  <Sun className="h-4 w-4" />
                                </span>
                              </TooltipTrigger>
                            </TabsTrigger>
                            <TooltipContent side="top">{t('theme.light')}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TabsTrigger value="dark" className="px-2">
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center justify-center">
                                  <Moon className="h-4 w-4" />
                                </span>
                              </TooltipTrigger>
                            </TabsTrigger>
                            <TooltipContent side="top">{t('theme.dark')}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TabsTrigger value="system" className="px-2">
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center justify-center">
                                  <Monitor className="h-4 w-4" />
                                </span>
                              </TooltipTrigger>
                            </TabsTrigger>
                            <TooltipContent side="top">{t('theme.system')}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TabsList>
                    </Tabs>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => {
                      await logout()
                      navigate('/login', { replace: true })
                    }}
                    disabled={logoutPending}
                    variant="destructive"
                    className="cursor-pointer"
                  >
                    <LogOut className="h-4 w-4 text-destructive" />
                    <span>{logoutPending ? t('auth.signingOut') : t('auth.signOut')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate('/login')}
            >
              {t('auth.signIn', 'Sign In')}
            </Button>
          )}
        </div>
      </div>
    </header>
  )

  const pageContent = (
    <>
      {headerContent}
      <div className="w-full px-4 py-4 md:px-6 md:py-6">
        <Outlet />
      </div>
    </>
  )

  if (isMobile) {
    return (
      <main className="min-h-screen w-screen overflow-x-hidden text-foreground bg-background">
        <div
          className={[
            'flex min-h-screen w-[calc(100vw+16rem)] transition-transform duration-300 will-change-transform',
            mobileSidebarOpen ? 'translate-x-0' : '-translate-x-64',
          ].join(' ')}
        >
          {sidebarContent}
          <div className="relative min-h-screen w-screen shrink-0 bg-background">
            {mobileSidebarOpen ? (
              <button
                type="button"
                aria-label="Close sidebar menu"
                className="absolute inset-0 z-30 bg-black/20"
                onClick={() => setMobileSidebarOpen(false)}
              />
            ) : null}
            {pageContent}
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="flex min-h-screen w-full text-foreground bg-background">
      {sidebarContent}
      <div className="relative min-h-screen min-w-0 w-full max-w-full flex-1 bg-background flex flex-col">
        {pageContent}
        <PublicFooter />
      </div>
    </main>
  )
}
